import {
  Badge,
  Button,
  describe,
  FieldControl,
  type SchemaFormField,
  toast,
} from '@servicetim/ui/components';
import { LoaderCircle } from 'lucide-react';
import { useId, useState } from 'react';
import { RailCard } from './rail-card';
import { useTicketContext } from './ticket-context';

const LABELS_FIELD: SchemaFormField = {
  key: 'labels',
  type: 'tags',
  label: 'Labels',
  required: false,
  readOnly: false,
  system: true,
  placeholder: 'Add a label…',
};

const sameLabels = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** Labels as chips; edits are staged locally and saved as one `PATCH`. */
export function LabelsCard() {
  const { ticket, patch, patching } = useTicketContext();
  const inputId = useId();
  const [draft, setDraft] = useState<string[]>(ticket.labels);
  const [base, setBase] = useState<string[]>(ticket.labels);
  if (!sameLabels(base, ticket.labels)) {
    // The ticket changed underneath (another save, a refetch): pick up the server's labels.
    setBase(ticket.labels);
    setDraft(ticket.labels);
  }
  const dirty = !sameLabels(draft, ticket.labels);

  const save = async () => {
    try {
      const saved = await patch({ labels: draft });
      if (saved) toast.success('Labels saved');
    } catch (e) {
      toast.error(describe(e).title);
    }
  };

  if (!ticket.canEdit) {
    return (
      <RailCard title="Labels">
        {ticket.labels.length === 0 ? (
          <span className="text-fg-muted">No labels</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {ticket.labels.map((label) => (
              <Badge key={label} variant="secondary">
                {label}
              </Badge>
            ))}
          </div>
        )}
      </RailCard>
    );
  }

  return (
    <RailCard
      title="Labels"
      actions={
        dirty ? (
          <Button size="sm" onClick={() => void save()} disabled={patching}>
            {patching ? <LoaderCircle className="animate-spin" /> : null}
            Save
          </Button>
        ) : null
      }
    >
      <FieldControl
        field={LABELS_FIELD}
        value={draft}
        onChange={(next) => setDraft(Array.isArray(next) ? (next as string[]) : [])}
        id={inputId}
        disabled={patching}
      />
    </RailCard>
  );
}
